import dayjs from "dayjs";
import { formatBuySellTxnTypeLabel } from "../../../utils/transactions/txnTypeDisplayLabel";
import { isEquitySellType } from "../../../utils/reports/equityTransactionTypes";
import { formatReportTypeColumnLabel } from "../../../utils/reports/reportDisplayLabels";
import {
  pickHoldingBenchmarkFields,
  withTxnBenchmarkFields,
} from "../../../utils/reports/transactionBenchmarkColumns";
import { withTxnMetadataFields } from "../../../utils/reports/transactionMetadataColumns";
import {
  attachInvestorMeta,
  buildUserMetaFromInvestors,
  type UserMeta,
} from "../mf/mfReportParsing";
import { AIF_CONSOLIDATED_REPORT } from "./aifReportConfig";

export { attachInvestorMeta, buildUserMetaFromInvestors, type UserMeta };

const formatEpochDate = (value: unknown): string => {
  if (value == null || value === "" || value === "-") return "-";
  const n = Number(value);
  if (Number.isFinite(n) && n > 0) {
    return dayjs(n).format("DD/MM/YYYY");
  }
  return String(value);
};

const reportBackendFieldOrDash = (value: unknown): unknown => {
  if (value === null || value === undefined || value === "") return "-";
  return value;
};

const formatConsolidatedClosingValue = (value: unknown): number | "-" => {
  if (value === null || value === undefined || value === "" || value === "-") {
    return "-";
  }
  const num =
    typeof value === "number"
      ? value
      : Number(String(value).replace(/[₹,\s]/g, ""));
  if (!Number.isFinite(num)) return "-";
  return num;
};

const formatAifConsolidatedDate = (value: unknown): string => {
  if (value === null || value === undefined || value === "" || value === "-") {
    return "-";
  }
  const asStr = String(value).trim();
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(asStr)) return asStr;
  const n = Number(value);
  if (Number.isFinite(n) && n > 0) {
    const ms = n < 1e11 ? n * 1000 : n;
    const d = dayjs(ms);
    if (d.isValid()) return d.format("DD/MM/YYYY");
  }
  const parsed = dayjs(asStr);
  if (parsed.isValid()) return parsed.format("DD/MM/YYYY");
  return asStr;
};

const formatAifConsolidatedMetric = (value: unknown): string => {
  if (value === null || value === undefined || value === "" || value === "-") {
    return "-";
  }
  const num = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(num)) return String(value);
  return num.toFixed(2);
};

const formatAifConsolidatedPercent = (value: unknown): string => {
  if (value === null || value === undefined || value === "" || value === "-") {
    return "-";
  }
  const num = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(num)) return String(value);
  return num.toFixed(2);
};

const getHoldingSchemeName = (row: Record<string, any>): string =>
  String(row.schemeName ?? row.name ?? "-");

const getHoldingTransactionList = (holding: Record<string, any>): any[] => {
  const list =
    holding.transactions ??
    holding.transaction ??
    holding.txns ??
    holding.trxn ??
    [];
  return Array.isArray(list) ? list : [];
};

const isSellTxn = (row: Record<string, any>): boolean => {
  const raw = String(
    row.transType ?? row.txnType ?? row.transactionType ?? row.transactiontype ?? "",
  ).trim();
  if (!raw) return false;
  return isEquitySellType(raw) || raw.toUpperCase() === "SELL";
};

const filterPositiveUnits = (rows: any[]) =>
  rows.filter((r) => Number(r?.units ?? r?.quantity ?? 0) > 0);

const filterRealizedTxnRows = (rows: any[]) =>
  rows.filter((r) => isSellTxn(r)).map((r) => ({
    ...r,
    type: formatReportTypeColumnLabel("Realized", "Realized"),
    displayTransType: "Realized",
  }));

const resolveHoldingCagr = (row: Record<string, any>): string | number => {
  const meta = row?.metadata ?? row?.meta ?? row?.metaData ?? {};
  for (const key of ["cagr", "cagrAif"] as const) {
    const val = row[key] ?? meta[key];
    if (val != null && val !== "") return val;
  }
  return row.cagr ?? "-";
};

const resolveHoldingAverageDays = (row: Record<string, any>): string | number => {
  const meta = row?.metadata ?? row?.meta ?? {};
  const val =
    row.averageDays ??
    row.averageDay ??
    row.days ??
    meta.averageDays ??
    meta.days;
  if (val == null || val === "") return "-";
  return val;
};

const normalizeUnrealizedHoldingRow = (row: Record<string, any>) => {
  const meta = row?.metadata ?? row?.meta ?? {};
  const schemeName = getHoldingSchemeName(row);
  return withTxnMetadataFields(
    withTxnBenchmarkFields({
      ...row,
      schemeName,
      type: formatReportTypeColumnLabel("Unrealized", "Unrealized"),
      purchaseDate: formatEpochDate(row.purchaseDate ?? meta.purchaseDate),
      units: row.units ?? row.quantity,
      amount: row.amount ?? row.investedAmount ?? row.costValue,
      currentAmount: row.currentAmount ?? row.currentValue,
      averagePrice:
        row.averagePrice ?? row.avgPrice ?? row.averageNav ?? meta.averagePrice,
      currentPrice:
        row.currentPrice ?? row.currentNAV ?? row.currentNav ?? meta.currentPrice,
      gainLoss:
        row.gainLoss ??
        row.unrealisedPNL ??
        row.unrealizedPNL ??
        row.unrealisedGainLoss,
      gainLossPerc:
        row.gainLossPerc ??
        row.gainLossPercentage ??
        row.unrealisedPNLPercentage,
      folioNumber: row.folioNumber ?? row.folioNo ?? row.folio,
      cagr: resolveHoldingCagr(row),
      days: resolveHoldingAverageDays(row),
      adjustedCostPrice:
        row.adjustedCostPrice ?? meta.adjustedCostPrice ?? row.adjCost,
    }),
  );
};

const normalizeRealizedRow = (row: Record<string, any>) => {
  const meta = row.metadata ?? row.metaData ?? row.meta ?? {};
  const schemeName = getHoldingSchemeName(row);
  // AIF sell txns store sell NAV in purchasePrice — never use it as buy NAV.
  const avgBuy =
    row.avgBuyPrice ??
    row.averageBuyPrice ??
    row.averagePrice ??
    meta.avgBuyPrice ??
    meta.averageBuyPrice ??
    meta.averagePrice;
  const purchaseNav =
    row.purchaseNAV ??
    row.purchaseNav ??
    (avgBuy != null && avgBuy !== "-" ? avgBuy : undefined) ??
    row.averagePrice ??
    meta.purchaseNAV ??
    meta.averagePrice;
  const currentPrice =
    row.currentPrice ?? row.currentNAV ?? row.currentNav ?? meta.currentPrice;
  const txnDateRaw =
    row.transactionDate ?? row.transactiondate ?? row.date ?? null;
  return withTxnMetadataFields(
    withTxnBenchmarkFields({
      ...row,
      schemeName,
      type: formatReportTypeColumnLabel("Realized", "Realized"),
      transactionDate: formatEpochDate(txnDateRaw),
      purchaseDate: formatEpochDate(
        row.purchaseDate ?? meta.purchaseDate ?? txnDateRaw,
      ),
      purchaseNAV: purchaseNav ?? "-",
      currentPrice: currentPrice ?? "-",
      avgBuyPrice: avgBuy ?? "-",
      saleValue: row.saleValue ?? row.amount ?? row.currentAmount,
      units: row.units ?? row.quantity,
      realisedPnl:
        row.realisedPnl ??
        row.realisedPnL ??
        row.realisedPNL ??
        row.realizedPnl ??
        row.gainLoss,
      realisedPNLPercentage:
        row.realisedPNLPercentage ??
        row.realisedPnLPercentage ??
        row.realizedPNLPercentage ??
        row.gainLossPerc ??
        row.gainLossPercentage,
      cagr: resolveHoldingCagr(row),
    }),
  );
};

const flattenAifHoldingsTransactions = (holdings: any[]): any[] => {
  const txns: any[] = [];
  for (const h of holdings || []) {
    if (!h || typeof h !== "object") continue;
    const schemeName = getHoldingSchemeName(h);
    const list = getHoldingTransactionList(h);
    if (!list.length) continue;
    const hMeta = h.metadata ?? h.metaData ?? h.meta ?? {};
    const holdingPurchaseDate = h.purchaseDate ?? hMeta.purchaseDate ?? null;
    const holdingAveragePrice =
      h.averagePrice ?? h.avgPrice ?? hMeta.averagePrice ?? null;
    const holdingBenchmarks = pickHoldingBenchmarkFields(h);
    for (const t of list) {
      const meta = t.metadata ?? t.metaData ?? t.meta ?? {};
      const txnDateRaw =
        t.transactionDate ?? t.transactiondate ?? t.date ?? t.trxnDate ?? null;
      txns.push(
        withTxnMetadataFields(
          withTxnBenchmarkFields({
            ...holdingBenchmarks,
            ...t,
            schemeName: t.schemeName ?? schemeName,
            folioNumber: t.folioNumber ?? h.folioNumber ?? h.folioNo,
            userId: t.userId ?? h.userId,
            memberName: t.memberName ?? h.memberName,
            transactionDate: formatEpochDate(txnDateRaw),
            transType: t.transType ?? t.txnType ?? t.transactionType,
            purchaseDate:
              t.purchaseDate ??
              meta.purchaseDate ??
              holdingPurchaseDate ??
              txnDateRaw,
            purchasePrice: t.purchasePrice ?? t.price,
            averagePrice: t.averagePrice ?? holdingAveragePrice,
            avgBuyPrice:
              t.avgBuyPrice ?? t.averageBuyPrice ?? holdingAveragePrice,
            purchaseNAV:
              t.purchaseNAV ?? t.purchaseNav ?? holdingAveragePrice,
            currentPrice: t.currentPrice ?? t.currentNAV ?? h.currentPrice,
            amount: t.amount ?? t.costValue ?? t.investedAmount,
            currentAmount: t.currentAmount ?? t.currentValue ?? h.currentAmount,
            adjustedCostPrice:
              t.adjustedCostPrice ??
              h.adjustedCostPrice ??
              hMeta.adjustedCostPrice,
            units: t.units ?? t.quantity,
            xirr: t.xirr ?? h.xirr,
            cagr: t.cagr ?? h.cagr ?? resolveHoldingCagr(h),
            days: t.days ?? t.avgDays,
          }),
        ),
      );
    }
  }
  return txns;
};

const normalizeAifTransactionRow = (row: Record<string, any>) => {
  const isSell = isSellTxn(row);
  const transTypeRaw = row.transType ?? row.txnType ?? row.transactionType;
  return {
    ...row,
    transType: formatBuySellTxnTypeLabel(String(transTypeRaw ?? "")),
    purchasePrice: row.purchasePrice ?? row.price,
    currentPrice: row.currentPrice ?? row.currentNAV ?? row.currentNav,
    amount: row.amount ?? row.costValue,
    currentAmount: isSell ? "-" : (row.currentAmount ?? row.currentValue),
    currentValue: isSell ? "-" : (row.currentValue ?? row.currentAmount),
    units: row.units ?? row.quantity,
    cagr: row.cagr ?? "-",
  };
};

const normalizeHoldingRowForAll = (row: Record<string, any>) => {
  const normalized = normalizeUnrealizedHoldingRow({
    ...row,
    type: "Unrealized",
  });
  const averageNav = normalized.averagePrice ?? normalized.averageNav;
  const currentNAV = normalized.currentPrice ?? normalized.currentNAV;
  return {
    ...normalized,
    folioNumber:
      normalized.folioNumber ?? row.folioNumber ?? row.folioNo ?? row.folio ?? "-",
    units: normalized.units ?? row.units ?? row.quantity,
    averageNav: averageNav ?? "-",
    currentNAV: currentNAV ?? "-",
    averagePrice: averageNav ?? normalized.averagePrice,
    currentPrice: currentNAV ?? normalized.currentPrice,
    costValue:
      normalized.amount ?? normalized.costValue ?? normalized.investedAmount,
    currentValue:
      normalized.currentAmount ?? normalized.currentValue ?? normalized.marketValue,
    gainLoss: normalized.gainLoss,
    gainLossPercentage:
      normalized.gainLossPerc ?? normalized.gainLossPercentage,
    xirr: normalized.xirr ?? row.xirr ?? "-",
    cagr: normalized.cagr ?? resolveHoldingCagr(row),
    days: normalized.days ?? resolveHoldingAverageDays(row),
  };
};

const normalizeTxnRowForAll = (row: Record<string, any>) => {
  const isSell = isSellTxn(row);
  const schemeName = getHoldingSchemeName(row);
  const meta = row?.metadata ?? row?.metaData ?? row?.meta ?? {};
  const normalized = normalizeRealizedRow({ ...row, schemeName });
  // Do not fall back to purchasePrice — AIF sells store sell NAV there.
  const averageNav =
    row.purchaseNAV ??
    row.purchaseNav ??
    row.averagePrice ??
    row.avgBuyPrice ??
    row.averageBuyPrice ??
    meta.purchaseNAV ??
    meta.averagePrice ??
    normalized.purchaseNAV;
  const currentNAV =
    row.currentPrice ??
    row.currentNAV ??
    row.currentNav ??
    meta.currentPrice ??
    meta.currentNAV ??
    normalized.currentPrice ??
    normalized.currentNAV;
  const pnl =
    row.realisedPnl ??
    row.realisedPNL ??
    row.realizedPnl ??
    row.realizedPNL ??
    row.realisedGainLoss ??
    row.realizedGainLoss ??
    normalized.realisedPnl;
  const pnlPct =
    row.realisedPNLPercentage ??
    row.realisedPnLPercentage ??
    row.realizedPNLPercentage ??
    row.realizedPnLPercentage ??
    normalized.realisedPNLPercentage;
  return {
    ...normalized,
    schemeName,
    type: formatReportTypeColumnLabel(
      isSell ? "Realized" : "Unrealized",
      isSell ? "Realized" : "Unrealized",
    ),
    folioNumber:
      row.folioNumber ?? row.folioNo ?? row.folio ?? normalized.folioNumber ?? "-",
    units: row.units ?? row.quantity ?? normalized.units,
    purchaseDate: normalized.purchaseDate,
    averageNav: averageNav ?? "-",
    currentNAV: currentNAV ?? "-",
    averagePrice: averageNav ?? row.averagePrice,
    currentPrice: currentNAV ?? row.currentPrice,
    costValue: isSell ? "-" : (row.amount ?? row.costValue),
    currentValue: row.currentValue ?? row.currentAmount ?? row.currentVal ?? "-",
    gainLoss: pnl ?? "-",
    gainLossPercentage: pnlPct ?? "-",
    xirr: row.xirr ?? meta.xirr ?? normalized.xirr ?? "-",
    cagr: normalized.cagr ?? resolveHoldingCagr(row),
    days: row.days ?? normalized.days ?? "-",
  };
};

const extractAifHoldings = (payload: any): any[] => {
  if (Array.isArray(payload?.holdings)) return payload.holdings;
  if (Array.isArray(payload?.data?.holdings)) return payload.data.holdings;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  return [];
};

const extractTotalHoldingInfo = (payload: any): Record<string, unknown> | null =>
  payload?.totalHoldingInfo ??
  payload?.totalHolding ??
  payload?.data?.totalHoldingInfo ??
  null;

const mapConsolidatedMetricsToRow = (
  fundName: string,
  metrics: Record<string, unknown>,
): Record<string, any> => {
  const m = metrics && typeof metrics === "object" ? metrics : {};
  const valuationRaw = Number(m.valuation) || 0;
  return {
    name: fundName || String(m.schemeName ?? m.name ?? "").trim() || "-",
    memberName: m.memberName ?? m.member_name,
    userId: m.userId ?? m.userid ?? m.user_id ?? m.memberId,
    vintage: reportBackendFieldOrDash((m as any).vintage),
    commitment: formatConsolidatedClosingValue(m.commitment),
    unfundedCommitment: formatConsolidatedClosingValue(m.unfundedCommitment),
    updatedDate: formatAifConsolidatedDate(m.updatedDate),
    amountCalled: formatConsolidatedClosingValue(m.amountCalled),
    calledPercentage: formatAifConsolidatedPercent(m.calledPercentage),
    amountDistributed: formatConsolidatedClosingValue(m.amountDistributed),
    distributedPercentage: formatAifConsolidatedPercent(m.distributedPercentage),
    recallableDistributions: formatConsolidatedClosingValue(m.recallableDistributions),
    balanceRecallableCapital: formatConsolidatedClosingValue(
      m.balanceRecallableCapital,
    ),
    costBasis: formatConsolidatedClosingValue(m.costBasis),
    costBasisAdjustement: formatConsolidatedClosingValue(m.costBasisAdjustement),
    taxBasis: formatConsolidatedClosingValue(m.taxBasis),
    investedCost: formatConsolidatedClosingValue(m.investedCost),
    valuation: formatConsolidatedClosingValue(m.valuation),
    valuationDate: formatAifConsolidatedDate(m.valuationDate),
    valuationPercentage: formatAifConsolidatedPercent(m.valuationPercentage),
    valuationPlusDistributionPercentage: formatAifConsolidatedPercent(
      m.valuationPlusDistributionPercentage,
    ),
    income: formatConsolidatedClosingValue(m.income),
    expenses: formatConsolidatedClosingValue(m.expenses),
    irr:
      m.irr === null || m.irr === undefined || m.irr === ""
        ? "-"
        : reportBackendFieldOrDash(m.irr),
    dpi: formatAifConsolidatedMetric(m.dpi),
    rvpi: formatAifConsolidatedMetric(m.rvpi),
    _sortValuation: valuationRaw,
  };
};

export const parseAifConsolidatedReportPayload = (
  payload: unknown,
): Record<string, any>[] => {
  const root = payload as Record<string, any>;
  const inner = root?.data ?? root;

  if (Array.isArray(inner)) {
    return inner
      .filter((row) => row && typeof row === "object")
      .map((row) =>
        mapConsolidatedMetricsToRow(
          String(
            (row as any).name ??
              (row as any).schemeName ??
              (row as any).fundName ??
              "",
          ).trim(),
          row as Record<string, unknown>,
        ),
      )
      .sort(
        (a, b) => Number(b._sortValuation ?? 0) - Number(a._sortValuation ?? 0),
      )
      .map(({ _sortValuation: _ignored, ...row }) => row);
  }

  if (!inner || typeof inner !== "object") {
    return [];
  }

  return Object.entries(inner as Record<string, Record<string, unknown>>)
    .map(([fundName, metrics]) => mapConsolidatedMetricsToRow(fundName, metrics ?? {}))
    .sort((a, b) => Number(b._sortValuation ?? 0) - Number(a._sortValuation ?? 0))
    .map(({ _sortValuation: _ignored, ...row }) => row);
};

export type ParsedAifReport = {
  rows: any[];
  totalHoldingInfo?: Record<string, unknown> | null;
  totalPages?: number;
  pageIndex?: number;
  totalRecords?: number;
};

export const parseAifReportResponse = (
  reportType: string,
  payload: any,
  userMeta: UserMeta,
  selectedUserIds: number[] = [],
): ParsedAifReport => {
  let rows: any[] = [];
  let totalHoldingInfo: Record<string, unknown> | null = null;
  let totalPages = 1;
  let pageIndex = 1;
  let totalRecords = 0;

  if (reportType === AIF_CONSOLIDATED_REPORT) {
    rows = parseAifConsolidatedReportPayload(payload);
    return {
      rows: attachInvestorMeta(rows, userMeta, selectedUserIds),
      totalHoldingInfo: null,
      totalPages: 1,
      pageIndex: 1,
      totalRecords: rows.length,
    };
  }

  switch (reportType) {
    case "Holding": {
      const holdings = extractAifHoldings(payload);
      rows = filterPositiveUnits(holdings).map(normalizeUnrealizedHoldingRow);
      totalHoldingInfo = extractTotalHoldingInfo(payload);
      break;
    }
    case "Realized": {
      const holdings = extractAifHoldings(payload);
      rows = filterRealizedTxnRows(flattenAifHoldingsTransactions(holdings)).map(
        normalizeRealizedRow,
      );
      totalHoldingInfo = extractTotalHoldingInfo(payload);
      totalPages = Number(payload?.totalPages) || 1;
      pageIndex = Number(payload?.pageIndex) || 1;
      totalRecords = rows.length;
      break;
    }
    case "All": {
      const holdings = extractAifHoldings(payload);
      totalHoldingInfo = extractTotalHoldingInfo(payload);
      const unrealized = filterPositiveUnits(holdings).map(normalizeHoldingRowForAll);
      const realized = filterRealizedTxnRows(
        flattenAifHoldingsTransactions(holdings),
      ).map(normalizeTxnRowForAll);
      rows = [...unrealized, ...realized];
      totalPages = Number(payload?.totalPages) || 1;
      pageIndex = Number(payload?.pageIndex) || 1;
      totalRecords = rows.length;
      break;
    }
    case "Transaction": {
      const holdings = extractAifHoldings(payload);
      totalHoldingInfo = extractTotalHoldingInfo(payload);
      rows = flattenAifHoldingsTransactions(holdings).map(
        normalizeAifTransactionRow,
      );
      totalPages = Number(payload?.totalPages) || 1;
      pageIndex = Number(payload?.pageIndex) || 1;
      totalRecords = rows.length;
      break;
    }
    default:
      rows = [];
  }

  return {
    rows: attachInvestorMeta(rows, userMeta, selectedUserIds),
    totalHoldingInfo,
    totalPages,
    pageIndex,
    totalRecords,
  };
};

export const mergeAifTransactionPages = (existing: any[], next: any[]) => {
  const seen = new Set(
    existing.map(
      (r) =>
        `${r.userId}-${r.schemeName}-${r.transactionDate}-${r.units}-${r.transType}`,
    ),
  );
  const merged = [...existing];
  for (const row of next) {
    const key = `${row.userId}-${row.schemeName}-${row.transactionDate}-${row.units}-${row.transType}`;
    if (!seen.has(key)) {
      seen.add(key);
      merged.push(row);
    }
  }
  return merged;
};
